import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, Plus, MoreVertical, Send, Ban, RefreshCw } from 'lucide-react';
import {
    getMessengerTemplates,
    createSimpleTemplate,
    submitMessengerTemplate,
    disableMessengerTemplate,
    type SimpleTemplatePayload,
} from '@/services/api/messenger-templates';
import type {
    MessengerTemplate,
    TemplateStatus,
} from '@/types/messenger-template';

const STATUS_LABEL: Record<TemplateStatus, string> = {
    DRAFT: 'Rascunho',
    PENDING: 'Pendente',
    APPROVED: 'Aprovado',
    REJECTED: 'Rejeitado',
    REAPPROVAL_REQUIRED: 'Reaprovação',
    DISABLED: 'Desativado',
};

const STATUS_VARIANT: Record<TemplateStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    DRAFT: 'secondary',
    PENDING: 'outline',
    APPROVED: 'default',
    REJECTED: 'destructive',
    REAPPROVAL_REQUIRED: 'outline',
    DISABLED: 'secondary',
};

const EMPTY_FORM: SimpleTemplatePayload = {
    meta_app_id: 0,
    template_name: '',
    language: 'pt_BR',
    category: 'UTILITY',
    image_url: '',
    body_text: '',
    button_text: '',
    button_url: '',
};

export default function MessengerTemplates() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [form, setForm] = useState<SimpleTemplatePayload>(EMPTY_FORM);

    const { data: templates = [], isLoading } = useQuery({
        queryKey: ['messenger-templates'],
        queryFn: getMessengerTemplates,
        staleTime: 1000 * 60,
    });

    const { mutate: create, isPending: creating } = useMutation({
        mutationFn: createSimpleTemplate,
        onSuccess: () => {
            toast({ title: 'Template criado e enviado para aprovação!' });
            queryClient.invalidateQueries({ queryKey: ['messenger-templates'] });
            setIsCreateOpen(false);
            setForm(EMPTY_FORM);
        },
        onError: () => {
            toast({ title: 'Erro ao criar template.', variant: 'destructive' });
        },
    });

    const { mutate: submit, isPending: submitting } = useMutation({
        mutationFn: (t: MessengerTemplate) => submitMessengerTemplate(t.id),
        onSuccess: (_, t) => {
            toast({ title: `"${t.template_name}" enviado para aprovação.` });
            queryClient.invalidateQueries({ queryKey: ['messenger-templates'] });
        },
        onError: () => {
            toast({ title: 'Erro ao submeter template.', variant: 'destructive' });
        },
    });

    const { mutate: disable, isPending: disabling } = useMutation({
        mutationFn: (t: MessengerTemplate) => disableMessengerTemplate(t.id),
        onSuccess: (_, t) => {
            toast({ title: `"${t.template_name}" desativado.` });
            queryClient.invalidateQueries({ queryKey: ['messenger-templates'] });
        },
        onError: () => {
            toast({ title: 'Erro ao desativar template.', variant: 'destructive' });
        },
    });

    const handleCreate = () => {
        if (!form.meta_app_id || !form.template_name.trim() || !form.image_url.trim() || !form.body_text.trim() || !form.button_text.trim() || !form.button_url.trim()) return;
        create(form);
    };

    const canSubmit = (status: TemplateStatus) =>
        status === 'DRAFT' || status === 'REAPPROVAL_REQUIRED';

    const canDisable = (status: TemplateStatus) => status === 'APPROVED';

    const isBusy = submitting || disabling;

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">Templates Messenger</h1>
                    <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                        <RefreshCw className="h-3 w-3" />
                        Sincronização automática a cada 10 min
                    </p>
                </div>
                <Button onClick={() => setIsCreateOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Template
                </Button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                    <Send className="h-8 w-8 opacity-40" />
                    <p>Nenhum template cadastrado</p>
                </div>
            ) : (
                <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 border-b">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium text-muted-foreground w-16">ID</th>
                                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Nome</th>
                                    <th className="text-left px-4 py-3 font-medium text-muted-foreground w-28">Categoria</th>
                                    <th className="text-left px-4 py-3 font-medium text-muted-foreground w-24">Idioma</th>
                                    <th className="text-left px-4 py-3 font-medium text-muted-foreground w-36">Status</th>
                                    <th className="text-left px-4 py-3 font-medium text-muted-foreground w-44">Sincronizado</th>
                                    <th className="w-12" />
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {templates.map((t) => (
                                    <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                                        <td className="px-4 py-3 text-muted-foreground">{t.id}</td>
                                        <td className="px-4 py-3 font-medium">{t.template_name}</td>
                                        <td className="px-4 py-3">
                                            <Badge variant="outline">{t.category}</Badge>
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground">{t.language}</td>
                                        <td className="px-4 py-3">
                                            <Badge variant={STATUS_VARIANT[t.status]}>
                                                {STATUS_LABEL[t.status]}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-3 text-muted-foreground">
                                            {t.synced_at
                                                ? new Date(t.synced_at).toLocaleString('pt-BR')
                                                : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        disabled={isBusy}
                                                        className="h-8 w-8"
                                                    >
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem
                                                        disabled={!canSubmit(t.status)}
                                                        onClick={() => submit(t)}
                                                    >
                                                        <Send className="h-4 w-4 mr-2" />
                                                        Enviar para Meta
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        disabled={!canDisable(t.status)}
                                                        onClick={() => disable(t)}
                                                        className="text-destructive focus:text-destructive"
                                                    >
                                                        <Ban className="h-4 w-4 mr-2" />
                                                        Desativar
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Novo Template</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label>App ID</Label>
                                <Input
                                    type="number"
                                    placeholder="19"
                                    value={form.meta_app_id || ''}
                                    onChange={(e) => setForm((f) => ({ ...f, meta_app_id: Number(e.target.value) }))}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>Idioma</Label>
                                <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pt_BR">Português (BR)</SelectItem>
                                        <SelectItem value="en_US">English (US)</SelectItem>
                                        <SelectItem value="es_ES">Español</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label>Nome do template</Label>
                            <Input
                                placeholder="shein_avaliadora_01"
                                value={form.template_name}
                                onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
                            />
                            <p className="text-xs text-muted-foreground">Letras minúsculas, números e underscore</p>
                        </div>
                        <div className="space-y-1">
                            <Label>URL da imagem</Label>
                            <Input
                                placeholder="https://..."
                                value={form.image_url}
                                onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label>Texto do corpo</Label>
                            <textarea
                                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                placeholder="Olá! Confira nossa oferta..."
                                value={form.body_text}
                                onChange={(e) => setForm((f) => ({ ...f, body_text: e.target.value }))}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label>Texto do botão</Label>
                                <Input
                                    placeholder="Saiba mais"
                                    value={form.button_text}
                                    onChange={(e) => setForm((f) => ({ ...f, button_text: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label>URL do botão</Label>
                                <Input
                                    placeholder="https://..."
                                    value={form.button_url}
                                    onChange={(e) => setForm((f) => ({ ...f, button_url: e.target.value }))}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setIsCreateOpen(false); setForm(EMPTY_FORM); }} disabled={creating}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={creating || !form.meta_app_id || !form.template_name.trim() || !form.image_url.trim() || !form.body_text.trim() || !form.button_text.trim() || !form.button_url.trim()}
                        >
                            {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {creating ? 'Enviando para Meta...' : 'Criar e enviar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
